import { lockedNav } from './ui/locked-nav.ts';
import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from './lit-base.ts';
import { StoreController } from './state/controller.ts';
import {
  activeTab,
  activePreset,
  endDate,
  startDate,
  type DatePreset,
  activeSection,
  catalogAvailable,
  activeDataSource,
  activeModelTab,
  selectedShip,
  ships,
  backendVersion,
  backendGitSha,
  myPermissions,
  myAccess,
  isRootUser,
  canDo,
  type Tab,
  type Section,
  type ModelTab,
  type DataSource,
} from './state/store.ts';
import { serverFlags } from './state/feature-flags.ts';
import {
  DEFAULT_SOURCE,
  dataSources,
  isSelectable,
  loadDataSources,
  unselectableReason,
  sourceHasHistory,
  sourceLabel,
} from './state/data-sources.ts';
import {
  isAdminRoute,
  isPageVisible,
  pageLock,
  pageVisibility,
  visibleTabs,
  type PageId,
} from './state/page-visibility.ts';
import { errorMessage } from './ui/error-box.ts';
import { syncToUrl } from './state/url-sync.ts';
import { loadShips } from './features/ship/ship-data.ts';
import { switchDataSource, type SwitchOptions } from './features/ship/data-source-step.ts';
import { BUILDER_EMBED, builderWindows } from './state/embed.ts';
import { installBuilderFrameApi } from './features/dashboard/builder-frame.ts';
import { startOver } from './state/start-over.ts';
import { initialSection } from './state/landing.ts';
import { parseAppRoute, sectionRoute } from './state/app-route.ts';
import { confirmAction } from './ui/confirm-dialog.ts';
import { showToast } from './ui/toast.ts';
import type { AssistantNavigation } from './api/types.ts';

import './features/ship/ship-picker.ts';
import './features/ship/date-range.ts';
import './features/ship/ship-multi-select.ts';
import { modelSelection } from './features/ship/model-selection.ts';
import './features/sfoc/sfoc-curve-picker.ts';
import './features/columns/column-selector.ts';
import './features/derived/derived-signals.ts';
import './features/filters/filter-builder.ts';
import './features/outliers/outlier-controls.ts';
import './features/export/output-panel.ts';
import './features/export/export-stats.ts';
import './features/export/export-panel.ts';
import './features/preview/data-preview.ts';
import './features/plot/plot-controls.ts';
import './features/plot/plot-action-bar.ts';
import './features/plot/plot-chart.ts';
import './features/presets/presets-bar.ts';
import './features/spmodel/spmodel-viewer.ts';
import './features/sfoc/sfoc-viewer.ts';
import './features/live-status/live-status-page.ts';
import './features/live-status/fleet-tabs.ts';
import './features/dashboard/dashboard-view.ts';
import './features/assistant/assistant-surface.ts';
import './features/notifications/notification-bell.ts';
import './features/announcements/announcements.ts';
import './features/announcements/update-strip.ts';
import './features/announcements/account-deactivated.ts';
import { assistantMark } from './features/assistant/mark.ts';
import {
  ASSISTANT_SECTION_LABEL,
  assistantDrawerOpen,
  assistantSurface,
  assistantTab,
  setAssistantTab,
  openAssistantDrawer,
  openAssistantSection,
  syncAssistantSurface,
  toggleAssistantDrawer,
} from './state/assistant-surface.ts';
import './features/catalog/catalog-page.ts';
// Argus hangs on the shell through the lens registry and nothing else: this
// import is what registers it, and removing the directory leaves an unclaimed
// lens name rather than a broken application. See `docs/plans/argus.md`, "Staying separate".
import './features/argus/argus-section.ts';
import {
  ARGUS_ROOMS,
  argusAttention,
  argusClock,
  argusRoom,
  hasArgus,
  setArgusRoom,
} from './features/argus/argus-lens.ts';
import { activeLens, publishLensAttribute, restoreLensFromUrl } from './state/lens.ts';
import {
  chartById,
  loadCatalog,
  openChart,
} from './features/catalog/catalog-state.ts';
import './features/home/home-page.ts';
import './features/catalog/chart-share-dialog.ts';
import './features/catalog/save-chart-dialog.ts';
import './features/tours/tour-overlay.ts';
import './features/tours/tour-welcome.ts';
import './features/tours/tours-panel.ts';
import './features/tours/tours/first-plot.ts';
import './features/tours/tours/first-dashboard.ts';
import './features/tours/tours/sfoc.ts';
import { toursPanelOpen } from './features/tours/tour-engine.ts';
import { availableTours } from './features/tours/tour-registry.ts';
import { openPreferences } from './features/preferences/preferences-panel.ts';
import {
  ASSISTANT_NAVIGATE_EVENT,
  planAssistantNavigation,
} from './features/assistant/navigation.ts';
import { OPEN_SECTION_EVENT } from './state/open-section.ts';
import './features/admin/admin-page.ts';
import './features/ui-gallery/ui-gallery.ts';
import { isUiGalleryRoute } from './features/ui-gallery/ui-gallery.ts';
import { closeDashboard, dashboardRoute } from './features/dashboard/dashboard-state.ts';
import { resizePlot } from './features/plot/render/chart-dom.ts';
import { rerenderChartFromLast } from './features/plot/plot-actions.ts';
import { authState, signIn } from './auth/auth.ts';
import './auth/user-menu.ts';
import { watchMe } from './state/me.ts';
import {
  periodComparison,
  resumeSuspendedPeriodComparison,
  stopPeriodComparison,
} from './state/period-comparison.ts';
import { userMessage } from './api/errors.ts';

const DIVIDER_MIN = 280;
const DIVIDER_MAX = 640;

// Easter egg - a little line-art cruise ship that matches the brand mark's
// stroke aesthetic. Sails the header once after ten clicks on the version chip.
// The flag is the carnival-maritime icon (served from /public), flown from the
// mast; the hull/decks are drawn to match the logo's 1.5px stroke look.
const VERSION_CLICKS_TO_SAIL = 10;
const SHIP_HTML = `
<img class="easter-ship-flag" src="/carnival-maritime.ico" alt="" aria-hidden="true" />
<svg width="56" height="32" viewBox="0 0 46 26" fill="none" aria-hidden="true">
  <path d="M3 15 H39 L34 21 H8 Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
  <path d="M9 15 V9 H33 V15" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
  <path d="M13 9 V5 H23 V9" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
  <path d="M26 9 V6 H29 V9" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
  <path d="M18 5 V1.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
  <circle cx="14" cy="12" r="0.85" fill="currentColor"/>
  <circle cx="18" cy="12" r="0.85" fill="currentColor"/>
  <circle cx="22" cy="12" r="0.85" fill="currentColor"/>
  <circle cx="26" cy="12" r="0.85" fill="currentColor"/>
</svg>`;

/**
 * Top-level layout shell. Header (title, tabs and error box), two-panel body
 * with a draggable divider. Mounts the ported feature components;
 * the not-yet-ported areas (columns, plot, export, presets) leave atom
 * seams and minimal placeholders that those areas fill in.
 */
/** The models, each a top-level Explore tab after Export, in tab order. */
const MODEL_TABS: Array<{ id: ModelTab; label: string }> = [
  { id: 'sfoc', label: 'SFOC' },
  { id: 'sp', label: 'Model Viewer' },
];

@customElement('app-root')
export class AppRoot extends LightElement {
  private store = new StoreController(this, [
    activeTab,
    activeSection,
    pageVisibility,
    activeDataSource,
    dataSources,
    activeModelTab,
    errorMessage,
    backendVersion,
    backendGitSha,
    authState,
    catalogAvailable,
    toursPanelOpen,
    // Permissions and feature flags decide which tabs are in the bar at all,
    // and both arrive after the first paint - so the header has to re-render on
    // them, not only on the atoms it reads directly.
    myPermissions,
    isRootUser,
    serverFlags,
    // Why a feature is off decides whether its word is struck through or absent.
    myAccess,
    // The button is lit while the assistant is showing, in any of its shapes.
    assistantDrawerOpen,
    assistantTab,
    // The lens repaints the shell, and the room tabs are part of the header.
    activeLens,
    argusRoom,
    argusClock,
    argusAttention,
  ]);

  /** Keeps `--app-header-h` in step with the header - see `firstUpdated`. */
  private headerObserver: ResizeObserver | null = null;
  private stopMeRefresh: (() => void) | null = null;

  // Divider-drag state.
  private dragging = false;
  /** The Data source step starts folded: there is one source to use today. */
  private onMouseMove = (e: MouseEvent): void => {
    if (!this.dragging) return;
    const twoPanel = this.querySelector<HTMLElement>('.two-panel');
    if (!twoPanel) return;
    const rect = twoPanel.getBoundingClientRect();
    let w = Math.round(e.clientX - rect.left);
    if (w < DIVIDER_MIN) w = DIVIDER_MIN;
    if (w > DIVIDER_MAX) w = DIVIDER_MAX;
    twoPanel.style.setProperty('--panel-width', w + 'px');
    resizePlot();
  };
  private onMouseUp = (): void => {
    if (!this.dragging) return;
    this.dragging = false;
    this.querySelector('#panel-divider')?.classList.remove('dragging');
    document.body.classList.remove('resizing-panel');
  };

  // Easter egg state: count clicks on the version in the profile menu (resetting if they slow down so it
  // only triggers on a deliberate burst) and let one ship sail at a time.
  private versionClicks = 0;
  private versionResetTimer: number | null = null;
  private shipSailing = false;

  private onAssistantNavigate = (event: Event): void => {
    event.stopPropagation();
    void this.applyAssistantNavigation(
      (event as CustomEvent<AssistantNavigation>).detail,
    );
  };

  private onOpenSection = (event: Event): void => {
    event.stopPropagation();
    this.switchSection((event as CustomEvent<Section>).detail);
  };

  private onVersionClick = (): void => {
    if (this.versionResetTimer !== null) clearTimeout(this.versionResetTimer);
    this.versionResetTimer = window.setTimeout(() => {
      this.versionClicks = 0;
      this.versionResetTimer = null;
    }, 1500);
    this.versionClicks += 1;
    if (this.versionClicks >= VERSION_CLICKS_TO_SAIL) {
      this.versionClicks = 0;
      clearTimeout(this.versionResetTimer);
      this.versionResetTimer = null;
      this.launchShip();
    }
  };

  /** Spawn the header ship for a single voyage, right edge to off the left side
   * (so the flag streams the right way for the inverted icon). */
  private launchShip(): void {
    if (this.shipSailing) return;
    const header = this.querySelector<HTMLElement>('.app-header');
    const brand = this.querySelector<HTMLElement>('.brand-mark');
    if (!header || !brand) return;
    this.shipSailing = true;

    const hRect = header.getBoundingClientRect();
    const shipW = 64; // hull plus a little flag overhang
    const startLeft = hRect.width - shipW - 8; // dock near the right edge
    // Sail leftward until fully clear of the left edge (negative distance).
    const distance = -(startLeft + shipW + 16);

    const ship = document.createElement('div');
    ship.className = 'easter-ship';
    ship.setAttribute('aria-hidden', 'true');
    ship.style.left = `${startLeft}px`;
    ship.style.setProperty('--sail-distance', `${distance}px`);
    ship.innerHTML = SHIP_HTML;
    ship.addEventListener('animationend', () => {
      ship.remove();
      this.shipSailing = false;
    });
    header.appendChild(ship);
  }

  override connectedCallback(): void {
    super.connectedCallback();
    if (BUILDER_EMBED) {
      // The chart builder's frame: Explore's Plot tab and nothing else.
      document.documentElement.classList.add('builder-embed');
      installBuilderFrameApi();
    }
    // Identity is needed on every route, including `/admin`, which replaces
    // the app and never loads a ship.
    // Permissions and flags land after the first paint and can add or remove a
    // tab (see `page-visibility`), so the "move off a hidden page" check runs
    // again once the answer is in - otherwise a deep link to a gated tab is
    // bounced a moment before that tab becomes legal.
    this.stopMeRefresh = watchMe(() => this.enforceVisibility());
    document.body.dataset.activeTab = activeTab.value;
    // Read the clean route (or a legacy query) before ship bootstrap so Home,
    // Dashboards, Catalog, and Assistant paint in the right section immediately.
    // `restoreFromUrl` runs after `loadShips()` and then applies the full saved
    // workspace without exposing it in the address bar.
    activeSection.set(initialSection(window.location.search, window.location.pathname));
    // A registered section may name a place inside it (`/argus/rules`).
    const launchRoute = parseAppRoute(window.location.pathname);
    if (launchRoute?.sub) sectionRoute(launchRoute.section)?.restoreSub?.(launchRoute.sub);
    this.enforceVisibility();
    // A lens in the link wins over the one this browser remembers, so a shared
    // view shows the reader the picture the sender was looking at. An unknown
    // or unavailable name lands on the plain application - `setLens` sees to
    // that - rather than on a half-drawn one.
    restoreLensFromUrl();
    publishLensAttribute();
    document.body.dataset.section = activeSection.value;
    document.body.dataset.activeTab = activeTab.value;
    document.body.dataset.modelTab = activeModelTab.value;
    syncAssistantSurface();
    document.body.dataset.datasource = activeDataSource.value;
    // A link may name a source this account cannot use (down, or its feature
    // off). Once the server has said, fall back to EOS and say why, rather
    // than leave Explore on a step whose every request is refused.
    void loadDataSources().then(() => {
      const ds = activeDataSource.value;
      if (ds === DEFAULT_SOURCE) return;
      const src = dataSources.value.find((s) => s.id === ds);
      if (src && isSelectable(src)) return;
      showToast('error', `${src ? unselectableReason(src) : `Unknown data source "${ds}".`} Showing ${sourceLabel(DEFAULT_SOURCE)} instead.`);
      void this.switchDataSource(DEFAULT_SOURCE);
    });
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);
    this.addEventListener(ASSISTANT_NAVIGATE_EVENT, this.onAssistantNavigate);
    this.addEventListener(OPEN_SECTION_EVENT, this.onOpenSection);
  }

  /**
   * Publish the header's height as `--app-header-h`.
   *
   * The assistant drawer is fixed under the header rather than over it, so the
   * utilities - including the glyph that opened it - stay reachable. The header
   * is two rows on Explore and one everywhere else, and it wraps on narrow
   * viewports, so the height is measured rather than assumed.
   *
   * Attached on the first render that *has* a header, not on the first render:
   * every session starts on the sign-in gate, which renders in place of the
   * whole shell. Observing once there and giving up left the drawer sitting at
   * its fallback offset for the rest of the session.
   */
  protected override updated(): void {
    if (this.headerObserver || typeof ResizeObserver === 'undefined') return;
    const header = this.querySelector<HTMLElement>('.app-header');
    if (!header) return;
    const publish = (): void => {
      document.documentElement.style.setProperty(
        '--app-header-h',
        `${Math.round(header.getBoundingClientRect().height)}px`,
      );
    };
    publish();
    this.headerObserver = new ResizeObserver(publish);
    this.headerObserver.observe(header);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.headerObserver?.disconnect();
    this.headerObserver = null;
    this.stopMeRefresh?.();
    this.stopMeRefresh = null;
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseup', this.onMouseUp);
    this.removeEventListener(ASSISTANT_NAVIGATE_EVENT, this.onAssistantNavigate);
    this.removeEventListener(OPEN_SECTION_EVENT, this.onOpenSection);
  }

  /**
   * Move off a page that `/admin` has hidden.
   *
   * Without this a link to a hidden tab - or simply the tab you were last on -
   * leaves the app on a page whose button is gone, with no way back to it and
   * nothing selected in the tab bar.
   */
  /**
   * Re-checked on every render, because the two things that decide visibility -
   * permissions and feature flags - arrive after the first paint, and
   * `restoreFromUrl` runs later still, behind `loadShips()`. Cheap, and it
   * converges: it only ever moves *to* a visible page.
   */
  protected override willUpdate(): void {
    this.enforceVisibility();
  }

  private enforceVisibility(): void {
    // The `body` attributes move with the atoms, because the per-section and
    // per-tab CSS keys off the attributes: leaving them behind showed a viewer
    // the Dashboards section they had just been moved out of, from a link.
    if (activeSection.value === 'dashboards' && !isPageVisible('dashboards')) {
      activeSection.set('explore');
      document.body.dataset.section = 'explore';
    }
    // Same for the assistant, which is a section now, plus the drawer it can
    // also be showing as - a flag turned off mid-session has to close both.
    if (!isPageVisible('assistant')) {
      if (activeSection.value === 'assistant') {
        activeSection.set('explore');
        document.body.dataset.section = 'explore';
      }
      if (assistantDrawerOpen.value) assistantDrawerOpen.set(false);
    }
    if (activeSection.value === 'live-status' && !isPageVisible('live-status')) {
      activeSection.set('explore');
      document.body.dataset.section = 'explore';
    }
    syncAssistantSurface();
    if (!isPageVisible(activeTab.value as PageId)) {
      const fallback = visibleTabs()[0];
      if (fallback) {
        activeTab.set(fallback.id as Tab);
        document.body.dataset.activeTab = fallback.id;
      }
    }
  }

  private switchTab(tab: Tab): void {
    if (tab === activeTab.value) return;
    if (activeTab.value === 'plot' && tab !== 'plot' && periodComparison.value) {
      stopPeriodComparison(
        tab === 'export'
          ? 'Exporting period A. Return to Plot to restore the comparison.'
          : 'Period comparison paused. Return to Plot to restore it.',
      );
    }
    activeTab.set(tab);
    document.body.dataset.activeTab = tab;
    if (tab === 'plot' && sourceHasHistory(activeDataSource.value)) {
      if (resumeSuspendedPeriodComparison()) rerenderChartFromLast();
    }
    syncToUrl();
    if (tab === 'plot') requestAnimationFrame(() => resizePlot());
  }

  /**
   * Move between primary sections through the same atom/body/clean-route seam
   * used by the contextual tabs.
   *
   * The chart is resized on the way back to Explore: it was laid out while the
   * two-panel grid was `display:none`, so uPlot measured nothing and would stay
   * at its fallback size until the next window resize.
   */
  private switchSection(section: Section): void {
    if (section === activeSection.value) {
      // Clicking the destination you are already on goes up a level rather than
      // nowhere: from an open dashboard back to the list of dashboards, which
      // is what the button names. Anywhere else it is genuinely a no-op.
      if (section === 'dashboards' && dashboardRoute.value === 'view') {
        closeDashboard();
        syncToUrl();
      }
      return;
    }
    if (section !== 'explore' && periodComparison.value) {
      stopPeriodComparison('Period comparison paused. Return to Explore → Plot to restore it.');
    }
    activeSection.set(section);
    document.body.dataset.section = section;
    syncAssistantSurface();
    if (section === 'explore' && activeTab.value === 'plot' && sourceHasHistory(activeDataSource.value)) {
      if (resumeSuspendedPeriodComparison()) rerenderChartFromLast();
    }
    syncToUrl();
    if (section === 'explore') requestAnimationFrame(() => resizePlot());
  }

  /**
   * The header glyph.
   *
   * It opens the drawer over whatever you are looking at, which is the point of
   * it: the question arrives while you are reading something. Pressed while the
   * full section is open, there is nothing under the conversation to open it
   * over, so it goes Home and opens the drawer there, keeping the same chat in
   * view. Returning to wherever the section was opened from landed people in
   * Explore - the fallback - which read as being thrown out of the assistant.
   */
  private onAssistantGlyph(): void {
    if (activeSection.value === 'assistant') {
      this.switchSection('home');
      openAssistantDrawer();
      return;
    }
    toggleAssistantDrawer();
  }

  /**
   * Apply one backend-validated destination through the same state transitions
   * as a user click. No URL, selector, or arbitrary command crosses this seam.
   */
  private async applyAssistantNavigation(
    action: AssistantNavigation | undefined,
  ): Promise<void> {
    const plan = action ? planAssistantNavigation(action) : null;
    if (!plan) {
      showToast('error', 'SONAR requested an unknown destination.');
      return;
    }
    if (plan.page && !isPageVisible(plan.page)) {
      showToast('error', `${plan.label} is not available for this account.`);
      return;
    }
    if (plan.section === 'catalog' && !catalogAvailable.value) {
      showToast('error', 'Catalog is not available in this deployment.');
      return;
    }

    if (plan.popup === 'preferences') {
      openPreferences();
      return;
    }
    if (plan.section === 'assistant') {
      if (plan.assistantTab) setAssistantTab(plan.assistantTab);
      openAssistantSection();
      return;
    }

    try {
      const keepConversationVisible = activeSection.value === 'assistant';
      if (action?.target === 'catalog_chart') {
        await loadCatalog();
        const chart = action.catalog_chart_id ? chartById(action.catalog_chart_id) : undefined;
        if (!chart) {
          showToast('error', 'That Catalog chart is no longer available.');
          return;
        }
        await openChart(chart);
        if (keepConversationVisible) openAssistantDrawer();
        return;
      }
      if (plan.section) this.switchSection(plan.section);
      if (keepConversationVisible) openAssistantDrawer();
      if (plan.historical && activeDataSource.value !== DEFAULT_SOURCE) {
        await this.switchDataSource(DEFAULT_SOURCE);
      }
      if (plan.tab) this.switchTab(plan.tab);
      if (plan.modelTab) this.switchModelTab(plan.modelTab);
    } catch (error) {
      const message = userMessage(error);
      showToast('error', `Could not open ${plan.label}: ${message}`);
    }
  }

  /**
   * The logo and title: home.
   *
   * Home is Explore. From Dashboards or the Catalog that is all a click does -
   * Explore comes back as it was left, because going home is not a request to
   * lose work. Clicked on Explore itself, "home" can only mean a fresh start,
   * so it asks first and then reloads clean (`start-over.ts`).
   *
   * It is a real link to `/`, so a middle- or ⌘-click still opens a fresh
   * Explore in a new tab without asking anything.
   */
  private async onHome(e: MouseEvent): Promise<void> {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    if (activeSection.value !== 'explore') {
      this.switchSection('explore');
      return;
    }
    const reset = await confirmAction({
      title: 'Start over?',
      message:
        'This clears everything in Explore - ship, dates, columns, chart settings and filters. ' +
        'Saved charts, views and dashboards are not affected.',
      confirmLabel: 'Start over',
      destructive: true,
    });
    if (reset) startOver();
  }

  private switchModelTab(sub: ModelTab): void {
    if (sub === activeModelTab.value) return;
    activeModelTab.set(sub);
    document.body.dataset.modelTab = sub;
    syncToUrl();
  }

  /** Switch source from code (a link, a restore); the step itself uses the same function. */
  private async switchDataSource(ds: DataSource): Promise<void> {
    if (ds === activeDataSource.value) return;
    await switchDataSource(ds);
    this.onDataSourceChanged();
  }

  /** A source without history reads on the Plot tab (Export and the Model Viewer read EOS only). */
  private onDataSourceChanged(): void {
    if (!sourceHasHistory(activeDataSource.value)) this.switchTab('plot');
  }

  private onDividerDown(e: MouseEvent): void {
    this.dragging = true;
    this.querySelector('#panel-divider')?.classList.add('dragging');
    document.body.classList.add('resizing-panel');
    e.preventDefault();
  }

  private toggleSidebar(): void {
    const collapsed = document.documentElement.classList.toggle('sidebar-collapsed');
    localStorage.setItem('sidebarCollapsed', collapsed ? '1' : '0');
    this.requestUpdate(); // refresh the toggle glyph/title
    // Let the grid settle, then resize the chart to fill the new width.
    requestAnimationFrame(() => resizePlot());
  }

  private renderHeader(): TemplateResult {
    const tab = activeTab.value;
    const section = activeSection.value;
    const err = errorMessage.value;
    return html`
      <header class="app-header">
        <div class="app-header-main">
          <div class="app-brand">
            <a
              class="app-home"
              href="/"
              title=${section === 'explore' ? 'Start over' : 'Go to Explore'}
              @click=${(e: MouseEvent) => void this.onHome(e)}
            >
              <span class="brand-mark" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 16C6 16 6.5 8 10 8s3 8 7 8c2.5 0 3-5 4-5"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linejoin="round"
                    stroke-linecap="round"
                  />
                  <circle cx="10" cy="8" r="1.8" fill="currentColor" />
                </svg>
              </span>
              <h1>EOS Data Explorer</h1>
            </a>
            <!-- Hidden for now; kept to bring back.
            <span class="data-source-note"
              >raw data from <strong>Neptune</strong> · enriched by <strong>EOS</strong></span
            >
            -->
          </div>

          <nav class="primary-nav" aria-label="Primary navigation">
            <button
              class="primary-nav-btn ${section === 'home' ? 'active' : ''}"
              aria-current=${section === 'home' ? 'page' : nothing}
              @click=${() => this.switchSection('home')}
            >
              Home
            </button>
            <fleet-nav
              .section=${section}
              @section-pick=${(e: CustomEvent<Section>) => this.switchSection(e.detail)}
            ></fleet-nav>
            <button
              class="primary-nav-btn ${section === 'explore' ? 'active' : ''}"
              aria-current=${section === 'explore' ? 'page' : nothing}
              @click=${() => this.switchSection('explore')}
            >
              Explore
            </button>
            ${isPageVisible('dashboards')
              ? html`<button
                  class="primary-nav-btn ${section === 'dashboards' ? 'active' : ''}"
                  data-tour="nav-dashboards"
                  aria-current=${section === 'dashboards' ? 'page' : nothing}
                  @click=${() => this.switchSection('dashboards')}
                >
                  Dashboards
                </button>`
              : pageLock('dashboards')
                ? lockedNav('Dashboards', pageLock('dashboards')!, 'primary-nav-btn')
                : nothing}
            <button
              class="primary-nav-btn ${section === 'catalog' ? 'active' : ''}"
              data-tour="nav-catalog"
              aria-current=${section === 'catalog' ? 'page' : nothing}
              title=${catalogAvailable.value
                ? 'Saved and shared charts'
                : 'The catalog needs the metadata database, which this deployment has not got'}
              @click=${() => this.switchSection('catalog')}
            >
              Catalog
            </button>
            <!-- The assistant is a destination as well as an action: this word
                 is the place - conversations, and what grows next to them - and
                 ASK in the utilities is the thing you do from any other page.
                 A noun and a verb, so the two never read as duplicates. -->
            ${isPageVisible('assistant')
              ? html`<button
                  class="primary-nav-btn ${section === 'assistant' ? 'active' : ''}"
                  aria-current=${section === 'assistant' ? 'page' : nothing}
                  title="Your conversations"
                  @click=${() => this.switchSection('assistant')}
                >
                  ${ASSISTANT_SECTION_LABEL}
                </button>`
              : pageLock('assistant')
                ? lockedNav(ASSISTANT_SECTION_LABEL, pageLock('assistant')!, 'primary-nav-btn')
                : nothing}
            <!-- The lens switch that sat in a hover card under ARGUS is out for
                 now (2026-10-22). See argus-lens.ts: the lens is paused. -->
            ${isPageVisible('argus')
              ? html`<span class="argus-nav">
                  <button
                    class="primary-nav-btn argus-nav-btn ${section === 'argus' ? 'active' : ''}"
                    aria-current=${section === 'argus' ? 'page' : nothing}
                    title="Deviation alerting"
                    @click=${() => this.switchSection('argus')}
                  >
                    ARGUS${this.argusCount('argus-nav-count')}
                  </button>
                </span>`
              : pageLock('argus')
                ? lockedNav('ARGUS', pageLock('argus')!, 'primary-nav-btn')
                : nothing}
          </nav>

          <div class="header-utilities">
            <!-- The assistant is reachable from every section, so its entry is
                 in the utilities rather than a fifth nav word: it opens over the
                 page you are on instead of replacing it. It is labelled, not a
                 lone glyph - nothing else in this header is a glyph on its own,
                 and an unlabelled mark is a guess for anyone who has not been
                 told what it does. -->
            ${isPageVisible('assistant')
              ? html`<button
                  class="assistant-toggle ${assistantSurface() === 'drawer' ||
                  assistantSurface() === 'mark'
                    ? 'active'
                    : ''}"
                  title="Ask SONAR about this page"
                  aria-expanded=${assistantSurface() !== 'closed'}
                  @click=${() => this.onAssistantGlyph()}
                >
                  ${assistantMark(20)}
                  <span>SONAR</span>
                </button>`
              : nothing}
            <!-- Guided tours: labelled, like SONAR, because a lone "?" does not
                 say that it starts a tour. -->
            ${availableTours().length
              ? html`<button
                  class="tours-toggle ${toursPanelOpen.value ? 'active' : ''}"
                  data-opens-tours
                  title="Guided tours: step by step, on the real page"
                  aria-expanded=${toursPanelOpen.value}
                  @click=${() => toursPanelOpen.set(!toursPanelOpen.value)}
                >
                  <span class="tours-toggle-glyph" aria-hidden="true">▶</span>
                  <span>Show me how</span>
                </button>`
              : nothing}
            <!-- The bell and the account disc travel together at the right
                 edge: both are about you rather than about the page, and the
                 bell's panel hangs off the same corner. -->
            <span class="header-account">
              <notification-bell></notification-bell>
              <!-- Identity lives furthest right, behind an initials disc: the
                   name and email are one click away instead of taking header width. -->
              <user-menu @version-click=${this.onVersionClick}></user-menu>
            </span>
          </div>
        </div>

        ${section === 'assistant'
          ? html`<div class="explore-header-row assistant-header-row">
              <nav class="tab-bar contextual-tabs" role="tablist" aria-label="Assistant">
                ${(
                  [
                    ['chat', 'Chat'],
                    ['history', 'History'],
                  ] as const
                ).map(
                  ([id, label]) => html`<button
                    role="tab"
                    aria-selected=${assistantTab.value === id}
                    class="context-tab tab-btn ${assistantTab.value === id ? 'active' : ''}"
                    data-tab-btn=${`assistant-${id}`}
                    @click=${() => setAssistantTab(id)}
                  >
                    ${label}
                  </button>`,
                )}
              </nav>
            </div>`
          : nothing}

        ${section === 'argus'
          ? html`<div class="explore-header-row argus-header-row">
              <nav class="tab-bar contextual-tabs" role="tablist" aria-label="Argus">
                ${ARGUS_ROOMS.filter((room) => room.id !== 'health' || canDo('argus:health')).map(
                  (room) => html`<button
                    role="tab"
                    aria-selected=${argusRoom.value === room.id}
                    class="context-tab tab-btn ${argusRoom.value === room.id ? 'active' : ''}"
                    data-tab-btn=${`argus-${room.id}`}
                    @click=${() => setArgusRoom(room.id)}
                  >
                    ${room.label}${room.id === 'inbox' ? this.argusCount('argus-tab-count') : nothing}${room.id === 'health'
                      ? html`<span class="admin-role-chip argus-admin-chip">Admin</span>`
                      : nothing}
                  </button>`,
                )}
              </nav>
              <!-- Where the data stands, said in the header rather than on a
                   card: every suspicion is only as current as the data behind
                   it, and Argus must never let that go unsaid. -->
              <span class="argus-clock">${argusClock.value}</span>
            </div>`
          : nothing}

        ${section === 'explore'
          ? html`<div class="explore-header-row">
              <nav class="tab-bar contextual-tabs" role="tablist" aria-label="Explore tools">
                ${isPageVisible('plot')
                  ? html`<button
                      role="tab"
                      aria-selected=${tab === 'plot'}
                      class="context-tab tab-btn ${tab === 'plot' ? 'active' : ''}"
                      data-tab-btn="plot"
                      @click=${() => this.switchTab('plot')}
                    >
                      Plot
                    </button>`
                  : pageLock('plot')
                    ? lockedNav('Plot', pageLock('plot')!, 'context-tab tab-btn')
                    : nothing}
                ${isPageVisible('export')
                  ? html`<button
                      role="tab"
                      aria-selected=${tab === 'export'}
                      class="context-tab tab-btn eos-only ${tab === 'export'
                        ? 'active'
                        : ''}"
                      data-tab-btn="export"
                      @click=${() => this.switchTab('export')}
                    >
                      Export
                    </button>`
                  : pageLock('export')
                    ? lockedNav('Export', pageLock('export')!, 'context-tab tab-btn')
                    : nothing}
                ${MODEL_TABS.map(({ id, label }) => {
                  // Each model is its own top-level tab. Inside, both are still
                  // tab=modelviewer + a model, so old links keep working.
                  const on = tab === 'modelviewer' && activeModelTab.value === id;
                  return isPageVisible('modelviewer')
                    ? html`<button
                        role="tab"
                        aria-selected=${on}
                        class="context-tab tab-btn eos-only ${on ? 'active' : ''}"
                        data-tab-btn=${id === 'sp' ? 'modelviewer' : id}
                        @click=${() => {
                          this.switchModelTab(id);
                          this.switchTab('modelviewer');
                        }}
                      >
                        ${label}
                      </button>`
                    : pageLock('modelviewer')
                      ? lockedNav(label, pageLock('modelviewer')!, 'context-tab tab-btn')
                      : nothing;
                })}
              </nav>

              <div class="contextual-actions">
                <presets-bar class="header-views"></presets-bar>
              </div>
            </div>`
          : nothing}
        <div class="error-box" style=${err ? 'display:block' : 'display:none'}>
          ${err ?? nothing}
        </div>
        <!-- Administrators' announcements: the strip closes the header, so it
             pushes the page down rather than covering what is on it. -->
        <announcement-strip></announcement-strip>
        <!-- Shown only after a deploy, until the tab is reloaded. -->
        <app-update-strip></app-update-strip>
      </header>
    `;
  }

  /**
   * The chart builder's frame (`state/embed.ts`): Explore's editor and chart,
   * without the header, the other sections or the Assistant. The host dialog
   * carries the title, pins, Cancel and Save.
   */
  private renderBuilder(): TemplateResult {
    const w = builderWindows;
    const options: SwitchOptions = {
      ...(w.livePreset ? { livePreset: w.livePreset as DatePreset } : {}),
      ...(w.historyStart && w.historyEnd
        ? {
            historyWindow: () => {
              startDate.set(w.historyStart!);
              endDate.set(w.historyEnd!);
              activePreset.set('custom');
            },
          }
        : {}),
    };
    return html`
      <div class="two-panel builder-two-panel">
        <div class="panel-left scroll-thin">
          <data-source-step .options=${options}></data-source-step>
          <div class="card">
            <h2>Query<span class="req-mark" title="Required - pick a ship and a date range">*</span></h2>
            <ship-picker class="explore-query"></ship-picker>
            <date-range class="explore-query"></date-range>
          </div>
          <plot-controls></plot-controls>
          <filter-builder></filter-builder>
          <derived-signals></derived-signals>
          <outlier-controls></outlier-controls>
          <plot-action-bar data-tab="plot"></plot-action-bar>
        </div>
        <div class="panel-divider" id="panel-divider" @mousedown=${this.onDividerDown}></div>
        <div class="panel-right scroll-thin">
          <plot-chart></plot-chart>
        </div>
      </div>
    `;
  }

  /** Open episodes nobody has marked: beside ARGUS, and on the Inbox tab. Absent at zero. */
  private argusCount(where: string): TemplateResult | typeof nothing {
    const { unseen, worst, followed = 0 } = argusAttention.value;
    if (!unseen && !followed) return nothing;
    // Rules the person follows count apart, dimmed: they read those alerts,
    // another team handles them (Argus decision 35).
    return html`${unseen
      ? html`<span
          class="glow-badge glow-alive ${where} is-${worst ?? 'watch'}"
          title=${`${unseen} open, not yet acknowledged`}
          >${unseen}</span
        >`
      : html`<span class="argus-glow-badge-none" hidden></span>`}${followed
      ? html`<span class="argus-count-followed ${where}" title=${`${followed} open on rules you follow`}
          >+${followed}</span
        >`
      : nothing}`;
  }

  private renderSidebarToggle(): TemplateResult {
    const collapsed = document.documentElement.classList.contains('sidebar-collapsed');
    return html`
      <button
        class="sidebar-toggle"
        title=${collapsed ? 'Show config panel' : 'Collapse config panel'}
        aria-label="Toggle config panel"
        aria-expanded=${!collapsed}
        @click=${() => this.toggleSidebar()}
      >
        <svg
          class="sidebar-toggle-icon"
          width="10"
          height="16"
          viewBox="0 0 10 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d=${collapsed ? 'M3 2.5 8 8l-5 5.5' : 'M7 2.5 2 8l5 5.5'}
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    `;
  }

  override render(): TemplateResult {
    const auth = authState.value;
    if (auth.status !== 'authenticated') {
      const waiting = auth.status === 'initializing' || auth.status === 'signing-in';
      // Every reload passes through `initializing`, and for someone already
      // signed in it lasts a fraction of a second - long enough to flash a
      // sign-in card at them on the way into the app. `restoring` holds the
      // card back briefly (auth.css), so it only appears if restoring the
      // session is genuinely slow, e.g. against a cold backend.
      const restoring = auth.status === 'initializing';
      return html`
        <main class="auth-gate">
          <section
            class="auth-card ${restoring ? 'restoring' : ''}"
            aria-live="polite"
            aria-busy=${waiting}
          >
            <img class="auth-logo" src="/favicon.svg" alt="" aria-hidden="true" />
            <h1>EOS Data Explorer</h1>
            <button
              class="btn btn-primary auth-sign-in"
              ?disabled=${waiting}
              @click=${() => void signIn()}
            >
              ${waiting ? 'Signing in…' : 'Sign in with Microsoft'}
            </button>
          </section>
        </main>
      `;
    }

    // `/admin` replaces the app rather than sitting inside it: it configures the
    // navigation, so rendering it within that navigation would be circular.
    // Keep this after the auth gate so the settings page is protected too.
    // The pop-up host comes along: an announcement is for every page, and
    // Administration previews its own pop-ups through it.
    if (isAdminRoute()) return html`<app-update-strip></app-update-strip><admin-page></admin-page><announcement-popup-host .arrivals=${false}></announcement-popup-host><account-deactivated></account-deactivated>`;

    // `/ui` is the inventory of every primitive, for the same reason `/admin`
    // is its own route: it is a different destination, not a view of the app.
    if (isUiGalleryRoute()) return html`<ui-gallery></ui-gallery>`;

    if (BUILDER_EMBED) return this.renderBuilder();

    return html`
      ${this.renderHeader()}
      <announcement-popup-host></announcement-popup-host>
      <account-deactivated></account-deactivated>

      <div class="two-panel">
        ${this.renderSidebarToggle()}
        <div class="panel-left scroll-thin">
          <data-source-step @data-source-changed=${() => this.onDataSourceChanged()}></data-source-step>
          <div class="card">
            <h2>Query<span class="req-mark" title="Required - pick a ship and a date range">*</span></h2>
            <!-- Explore's selection, and the Model Viewer's: the same controls
                 in the same place, pointed at different ships and windows.
                 CSS shows one pair per tab. ship-picker stays mounted either
                 way - it owns boot. -->
            <ship-picker class="explore-query" data-tour="ship"></ship-picker>
            <date-range class="explore-query" data-tour="dates"></date-range>
            <div class="model-query">
              <div class="ship-row" data-tour="sfoc-ships">
                <label class="ship-label">Ship</label>
                <ship-multi-select .scope=${modelSelection}></ship-multi-select>
              </div>
              <date-range .scope=${modelSelection} data-tour="sfoc-dates"></date-range>
            </div>
          </div>
          <sfoc-curve-picker data-tab="modelviewer-sfoc"></sfoc-curve-picker>

          <plot-controls></plot-controls>
          <filter-builder></filter-builder>
          <derived-signals></derived-signals>
          <outlier-controls></outlier-controls>

          <output-panel></output-panel>
          <export-stats></export-stats>
          <export-panel></export-panel>

          <!-- Last child of the scrollable flex column on purpose: auto margin
               reaches the bottom for short configurations, while sticky
               positioning keeps it there when the panel scrolls. -->
          <plot-action-bar data-tab="plot"></plot-action-bar>
        </div>

        <div
          class="panel-divider"
          id="panel-divider"
          @mousedown=${this.onDividerDown}
        ></div>

        <div class="panel-right scroll-thin">
          <data-preview></data-preview>
          <column-selector></column-selector>
          <plot-chart data-tour="chart"></plot-chart>
          <spmodel-viewer data-tab="modelviewer-sp"></spmodel-viewer>
          <sfoc-viewer data-tab="modelviewer-sfoc"></sfoc-viewer>
        </div>
      </div>

      <home-page></home-page>
      <dashboard-view></dashboard-view>
      <catalog-page></catalog-page>
      <argus-section></argus-section>
      <fleet-tabs
        .section=${activeSection.value}
        @section-pick=${(e: CustomEvent<Section>) => this.switchSection(e.detail)}
      ></fleet-tabs>
      <live-status-page></live-status-page>
      <assistant-surface></assistant-surface>
      <save-chart-dialog></save-chart-dialog>
      <preferences-panel></preferences-panel>
      <tours-panel></tours-panel>
      <tour-welcome></tour-welcome>
      <app-tour></app-tour>

    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'app-root': AppRoot;
  }
}
